/**
 * Add/Edit cancellation-band dialog (v2 — Project Independence, DNKADM-0057 U2).
 *
 * A MUI `Dialog` that configures one refund band: which bucket it applies to, the
 * minimum hours before start it qualifies at, and the refund percent.
 *
 * **Bucket control = segmented "All buckets" + the 0052 fail-closed credit-bucket
 * picker.** A `ToggleButtonGroup` chooses between "All buckets" (→ `creditBucketId:
 * null`, `scope=ALL`) and "Specific bucket" (→ `scope=BUCKET` + the picked id); the
 * specific branch reveals the live, fail-closed `useCreditBucketPickList` (no cache; a
 * load failure shows error + Retry inside the modal, never a partial/empty picker). The
 * band's `scope` is DERIVED from the bucket selection — the admin never picks it.
 *
 * **Validation (client, mirrors §5.12):** `minHoursBeforeStart` is a whole number ≥ 0;
 * `refundPercent` is `0.00–100.00` with up to two decimals and is **capped at 100**.
 * An **inline overlap warning** appears when the in-progress `(bucket,
 * minHoursBeforeStart)` collides with an existing OTHER band — it **warns, it does not
 * block submit** (the server's uniqueness `400`/`409`, if any, then surfaces inline).
 *
 * On a successful create/update the parent **refetches** the bands list (via the
 * caller's `onSaved`).
 */
import { useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { PickListLoader } from '@/components/PickListLoader';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import {
  createCancellationBand,
  updateCancellationBand,
  type CancellationBand,
  type CancellationBandInput,
} from '@/api/policies';
import type { ShowToast } from '@/components/toastContext';
import { collidesWithExisting } from './bandOverlap';
import { classifyFormError } from '@/api/screenError';

export interface BandDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The band being edited, or `null` to add a new one. */
  band: CancellationBand | null;
  /** All current bands — used for the inline overlap warning (excluding the edited band). */
  bands: readonly CancellationBand[];
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

/** The segmented bucket-scope choice: all buckets, or one specific bucket. */
type Scope = 'all' | 'specific';

interface FieldErrors {
  bucket?: string;
  minHoursBeforeStart?: string;
  refundPercent?: string;
}

/** Parse a refund-percent entry: ≥ 0, ≤ 2 decimals, capped at 100. */
function parsePercent(
  raw: string,
): { ok: true; value: number } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, message: 'Enter a refund percent (0–100).' };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, message: 'Enter 0–100 with up to two decimals.' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, message: 'Enter 0–100 with up to two decimals.' };
  }
  // Cap at 100 rather than reject — the field is capped, per spec.
  return { ok: true, value: Math.min(n, 100) };
}

export function BandDialog(props: BandDialogProps) {
  // Remount the inner form per open / per edited band so its inputs reseed cleanly
  // (mirrors PlanDialog's keyed remount). The bucket picker reloads live each open.
  if (!props.open) return null;
  return <BandDialogForm key={props.band?.id ?? 'new'} {...props} />;
}

function BandDialogForm({
  band,
  bands,
  onClose,
  onSaved,
  showToast,
}: Omit<BandDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = band !== null;

  const [scope, setScope] = useState<Scope>(
    band && band.creditBucketId !== null ? 'specific' : 'all',
  );
  const [bucketId, setBucketId] = useState(band?.creditBucketId ?? '');
  const [minHours, setMinHours] = useState(band ? String(band.minHoursBeforeStart) : '');
  const [percent, setPercent] = useState(band ? String(band.refundPercent) : '');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Live, fail-closed 0052 bucket picker — reloads on each mount (each dialog open).
  const buckets = useCreditBucketPickList();

  // The effective bucket scope from the current inputs (null = all buckets).
  const effectiveBucketId: string | null = scope === 'all' ? null : bucketId.trim() || null;

  // Inline overlap warning (warn, never block): the in-progress (bucket, hours) pair
  // collides with an existing OTHER band. Only meaningful once hours parse.
  const overlaps = useMemo(() => {
    const parsedHours = Number(minHours);
    if (
      minHours.trim() === '' ||
      !Number.isInteger(parsedHours) ||
      parsedHours < 0 ||
      (scope === 'specific' && effectiveBucketId === null)
    ) {
      return false;
    }
    return collidesWithExisting(bands, effectiveBucketId, parsedHours, band?.id ?? null);
  }, [bands, minHours, scope, effectiveBucketId, band]);

  function validate(): CancellationBandInput | null {
    const errors: FieldErrors = {};

    if (scope === 'specific' && effectiveBucketId === null) {
      errors.bucket = 'Select a bucket, or choose "All buckets".';
    }

    const hours = Number(minHours);
    if (
      minHours.trim() === '' ||
      !Number.isFinite(hours) ||
      !Number.isInteger(hours) ||
      hours < 0
    ) {
      errors.minHoursBeforeStart = 'Enter a whole number of hours, 0 or more.';
    }

    const parsedPercent = parsePercent(percent);
    if (!parsedPercent.ok) {
      errors.refundPercent = parsedPercent.message;
    }

    if (errors.bucket || errors.minHoursBeforeStart || errors.refundPercent) {
      setFieldErrors(errors);
      return null;
    }

    setFieldErrors({});
    return {
      creditBucketId: effectiveBucketId,
      minHoursBeforeStart: hours,
      // parsedPercent is ok here (no refundPercent error set).
      refundPercent: parsedPercent.ok ? parsedPercent.value : 0,
    };
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    // Overlap does NOT block — `input` is null only on a hard validation failure.
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && band && band.id !== null) {
        await updateCancellationBand(band.id, input);
      } else {
        await createCancellationBand(input);
      }
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Band saved' : 'Band added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(err, 'That band isn’t valid — check the bucket, hours, and refund percent.', {
        toastFallback: 'The band couldn’t be saved. Please try again.',
      });
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const scopeLabelId = `${ids}-scope-label`;
  const bucketId_ = `${ids}-bucket`;
  const hoursId = `${ids}-hours`;
  const percentId = `${ids}-percent`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit band' : 'Add band'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Bucket scope — segmented "All buckets" + the fail-closed bucket picker. */}
          <Box>
            <Typography
              id={scopeLabelId}
              component="div"
              sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}
            >
              Applies to
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={scope}
              aria-labelledby={scopeLabelId}
              onChange={(_e, v: Scope | null) => {
                // Exclusive group fires `null` when the active button is re-clicked;
                // ignore it so a scope is always selected.
                if (v) setScope(v);
              }}
              disabled={saving}
              sx={{ mb: scope === 'specific' ? 1.5 : 0 }}
            >
              <ToggleButton value="all">All buckets</ToggleButton>
              <ToggleButton value="specific">Specific bucket</ToggleButton>
            </ToggleButtonGroup>

            {scope === 'specific' && (
              <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
                {(options) => (
                  <TextField
                    id={bucketId_}
                    select
                    fullWidth
                    size="small"
                    hiddenLabel
                    value={options.some((o) => o.id === bucketId) ? bucketId : ''}
                    onChange={(e) => setBucketId(e.target.value)}
                    disabled={saving}
                    error={Boolean(fieldErrors.bucket)}
                    helperText={fieldErrors.bucket}
                    slotProps={{
                      htmlInput: { 'aria-label': 'Credit bucket' },
                    }}
                  >
                    {options.length === 0 && (
                      <MenuItem value="" disabled>
                        No credit buckets available
                      </MenuItem>
                    )}
                    {options.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name === '' ? o.id : o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </PickListLoader>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              id={hoursId}
              label="Cancel at least"
              value={minHours}
              onChange={(e) => setMinHours(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.minHoursBeforeStart)}
              helperText={fieldErrors.minHoursBeforeStart}
              sx={{ flex: 1 }}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">h before</InputAdornment> },
                htmlInput: { inputMode: 'numeric', 'aria-label': 'Cancel at least (hours before start)' }
              }} />
            <TextField
              id={percentId}
              label="Refund"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.refundPercent)}
              helperText={fieldErrors.refundPercent}
              sx={{ flex: 1 }}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { inputMode: 'decimal', 'aria-label': 'Refund percent' }
              }} />
          </Stack>

          {/* Inline overlap warning — warns, does NOT block submit. */}
          {overlaps && (
            <Alert severity="warning" icon={<Icon name="warning" />} role="status">
              Another band already covers this bucket at these hours. The system uses the
              largest qualifying threshold, so this duplicate may not take effect.
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save band' : 'Add band'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
